import React, { useMemo, useState } from 'react';
import type { GlobalProps } from '@bloomberg/media-ui-core';
import { classes, useSlug } from '@bloomberg/media-ui-core';

import { type DropdownOption, TickerBarWithPill } from '../TickerBarWithPill';
import type { TickerSecurity } from '../types';
import { useTranslateMessage } from '../utils/translate-message';

import {
  DEFAULT_LIKE_TICKER_SETS,
  UK_LIKE_TICKER_SETS,
} from './TickerBarWithPillWrapperUtil';

import styles from './TickerBarWithPillWrapper.module.css';

export type TickerBarWithPillWrapperProps = GlobalProps & {
  tickerBar: TickerSecurity[];
  isUk?: boolean;
  trackClick?: (eventAction: string, eventLabel: string) => void;
};

declare global {
  interface Window {
    dataLayer: any;
  }
}

export const TickerBarWithPillWrapper = React.forwardRef<
  HTMLDivElement,
  TickerBarWithPillWrapperProps
>(function TickerBarWithPillWrapperComponent(
  props: TickerBarWithPillWrapperProps,
  ref
) {
  const {
    tickerBar,
    isUk = false,
    trackClick,
    name = 'ticker-bar-with-pill-wrapper',
  } = props;

  const translateMessage = useTranslateMessage();

  const SECURITY_CATEGORIES = [
    {
      displayValue: translateMessage({
        id: 'media-ui-markets-top-securities',
        defaultMessage: 'Top Securities',
      }),
      value: 'top',
    },
    {
      displayValue: translateMessage({
        id: 'media-ui-markets-market-indices',
        defaultMessage: 'Market Indices',
      }),
      value: 'indices',
    },
    {
      displayValue: translateMessage({
        id: 'media-ui-markets-rates-and-bonds',
        defaultMessage: 'Rates & Bonds',
      }),
      value: 'rates',
    },
  ];

  const DEFAULT_TICKER_CATEGORY = SECURITY_CATEGORIES[0]?.value ?? 'top';
  const TICKER_CATEGORY_OPTIONS = useMemo(
    () => new Set(SECURITY_CATEGORIES.map(({ value: option }) => option)),
    [SECURITY_CATEGORIES]
  );

  const componentName = useSlug(name);
  const tickerSet = useMemo(
    () => (isUk ? UK_LIKE_TICKER_SETS : DEFAULT_LIKE_TICKER_SETS),
    [isUk]
  );
  const [currentTickerSet, setCurrentTickerSet] = useState(
    DEFAULT_TICKER_CATEGORY
  );
  const currentTickers = useMemo(
    () => new Set(tickerSet[currentTickerSet] || []),
    [currentTickerSet, tickerSet]
  );

  const currentTickerBar = useMemo(() => {
    return tickerBar?.filter(({ ticker }) => currentTickers.has(ticker));
  }, [currentTickers, tickerBar]);

  const onSecuritiesUpdate = (security?: DropdownOption | null) => {
    if (!security?.value) {
      return;
    }

    const isValid = TICKER_CATEGORY_OPTIONS.has(security?.value);

    if (!isValid) {
      return;
    }

    setCurrentTickerSet(security.value);
  };

  const cx = classes([styles.tickerbarWrapper], props);

  return (
    <div className={cx} ref={ref} data-component={componentName}>
      <TickerBarWithPill
        displaySecurityName
        trackClick={trackClick}
        tickers={currentTickerBar}
        securities={SECURITY_CATEGORIES}
        onSecuritiesUpdate={onSecuritiesUpdate}
      />
    </div>
  );
});
